import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";

/**
 * Mutators for user_presence — the hot-mutating presence/typing/status/
 * terminal columns split out of the users table in Phase 1.5 of the Zero
 * perf rollout (~/.claude/plans/zero-perf-rollout.md).
 *
 * Auth: Pattern 1 (userId-match) per .claude/rules/mutators.md. A user
 * can only mutate their own presence row. The check fires on BOTH client
 * and server (PushProcessor runs server-side with `tx.location ===
 * "server"`, but `ctx.sub` is still derived from the caller's JWT).
 *
 * Server-initiated operations that need to act on behalf of any user
 * (e.g. presence cleanup on disconnect from server/index.ts) MUST use
 * direct DB writes via `pg.Pool`, NOT these mutators. Relaxing the auth
 * here would allow any authenticated client to spoof another user's
 * presence by setting `args.user_id` to that user's id.
 *
 * All mutators below use `tx.mutate.user_presence.update(...)` and
 * therefore assume the row exists. The migration in PR 1.5.1 backfills
 * a user_presence row for every existing user; new users created after
 * that need to have a presence row inserted alongside (PR 1.5.2 handles
 * this in `coworker-create.ts` and `guest-routes.ts` via direct SQL
 * INSERT in the same transaction as the users INSERT).
 */

export const userPresenceMutators = {
  /** Set the caller's typing-in-channel marker. */
  setTyping: defineMutator(
    z.object({
      user_id: z.string(),
      channel_id: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      await tx.mutate.user_presence.update({
        user_id: args.user_id,
        typing_in: args.channel_id,
        typing_at: Date.now(),
      });
    },
  ),

  /** Clear the caller's typing marker. */
  clearTyping: defineMutator(
    z.object({ user_id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      await tx.mutate.user_presence.update({
        user_id: args.user_id,
        typing_in: null,
        typing_at: null,
      });
    },
  ),

  /** Heartbeat: caller is alive. Updates last_active_at. */
  heartbeat: defineMutator(
    z.object({ user_id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      await tx.mutate.user_presence.update({
        user_id: args.user_id,
        last_active_at: Date.now(),
      });
    },
  ),

  /** Mark the caller as online + bump last_active_at. */
  setOnline: defineMutator(
    z.object({ user_id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      await tx.mutate.user_presence.update({
        user_id: args.user_id,
        is_online: true,
        last_active_at: Date.now(),
      });
    },
  ),

  /** Mark the caller as offline + clear typing. */
  setOffline: defineMutator(
    z.object({ user_id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      await tx.mutate.user_presence.update({
        user_id: args.user_id,
        is_online: false,
        last_active_at: Date.now(),
        typing_in: null,
        typing_at: null,
      });
    },
  ),

  /**
   * Set status fields. Each field is optional — omit to leave unchanged
   * (Zero update semantics). Pass null to clear a specific field while
   * leaving the others alone. UserProfileFooter uses this to set status
   * text without disturbing emoji/expiry.
   */
  setStatus: defineMutator(
    z.object({
      user_id: z.string(),
      status: z.string().nullable().optional(),
      status_emoji: z.string().nullable().optional(),
      status_expiry: z.number().nullable().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const update: Record<string, unknown> = { user_id: args.user_id };
      if (args.status !== undefined) update.status = args.status;
      if (args.status_emoji !== undefined)
        update.status_emoji = args.status_emoji;
      if (args.status_expiry !== undefined)
        update.status_expiry = args.status_expiry;
      await tx.mutate.user_presence.update(
        update as Parameters<typeof tx.mutate.user_presence.update>[0],
      );
    },
  ),

  clearStatus: defineMutator(
    z.object({ user_id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      await tx.mutate.user_presence.update({
        user_id: args.user_id,
        status: null,
        status_emoji: null,
        status_expiry: null,
      });
    },
  ),
};
